import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '@akillidernek/api-client'
import { Button, Card, CardBody, CardHeader, Field, Input, useToast } from '@akillidernek/ui'
import { api } from '../api'
import { useTenantSlug } from '../use-tenant'
import { AyarlarSekmeleri } from './ayarlar-sekmeler'

/**
 * Ayarlar › Dernek bilgileri — YES-174 (kurulum sihirbazının ilk adımı).
 *
 * Makbuz ve e-Alındı belgelerinde görünen bilgiler burada: vergi dairesi ve
 * numarası boşsa makbuz eksik çıkar, o yüzden alanlar ayrı başlık altında.
 */
export function AyarlarDernek() {
  const slug = useTenantSlug()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [hatalar, setHatalar] = useState<Record<string, string>>({})

  const dernek = useQuery({
    queryKey: ['tenant', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/tenant')
      return data
    },
  })

  // Otomatik atama seçenekleri (YES-168): kişi ya da rol.
  const kullanicilar = useQuery({
    queryKey: ['tenant-users', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/users')
      return data ?? []
    },
  })
  const roller = useQuery({
    queryKey: ['roles', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/roles')
      return data ?? []
    },
  })

  const kaydet = useMutation({
    mutationFn: async (form: FormData) => {
      const oku = (ad: string) => String(form.get(ad) ?? '').trim() || null
      const saat = (ad: string) => Number(form.get(ad) ?? 0)
      const atama = String(form.get('auto_assign') ?? '')
      await api.PATCH('/api/v1/tenant', {
        body: {
          name: String(form.get('name') ?? '').trim(),
          settings: {
            // Renk ve logo bu formda değil (tema düzenleyici, YES-112);
            // gönderilmezse sunucu mevcut değerleri korur.
            ...(dernek.data?.settings ?? {}),
            contact_email: oku('contact_email'),
            contact_phone: oku('contact_phone'),
            address: oku('address'),
            tax_office: oku('tax_office'),
            tax_number: oku('tax_number'),
            require_mfa: form.get('require_mfa') !== null,
            fee_reminders_enabled: form.get('fee_reminders_enabled') !== null,
            support_sla: Object.fromEntries(
              ONCELIKLER.map(([kod]) => [
                kod,
                {
                  first_response_hours: saat(`sla_${kod}_first`),
                  resolution_hours: saat(`sla_${kod}_resolution`),
                },
              ]),
            ),
            support_auto_assign: atama.startsWith('user:')
              ? { user_id: atama.slice(5), role_code: null }
              : atama.startsWith('role:')
                ? { user_id: null, role_code: atama.slice(5) }
                : null,
          },
        },
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenant', slug] })
      // Sihirbaz adımı "Dernek bilgileri" bu formdan tamamlanır.
      void queryClient.invalidateQueries({ queryKey: ['onboarding', slug] })
      setHatalar({})
      toast({ title: 'Dernek bilgileri kaydedildi', tone: 'success' })
    },
    onError: (error) => {
      if (error instanceof ApiError) setHatalar(error.fieldErrors())
      toast({
        title: 'Kaydedilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      })
    },
  })

  if (dernek.isError) {
    const yetkisiz = dernek.error instanceof ApiError && dernek.error.problem.status === 403
    return (
      <Sayfa>
        <Card>
          <CardBody className="text-sm">
            {yetkisiz ? 'Dernek bilgilerini görme yetkiniz yok.' : 'Bilgiler yüklenemedi.'}
          </CardBody>
        </Card>
      </Sayfa>
    )
  }

  const veri = dernek.data
  const ayar = veri?.settings

  return (
    <Sayfa>
      <Card>
        <CardHeader
          title="Dernek bilgileri"
          description="Panel başlığında, üyelere giden e-postalarda ve makbuzlarda görünür."
        />
        <CardBody>
          {veri && (
            <form
              // `key`: veri yenilenince kontrolsüz alanlar yeni değerle kurulsun.
              key={veri.updated_at}
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault()
                kaydet.mutate(new FormData(event.currentTarget))
              }}
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Dernek adı" required error={hatalar.name}>
                  {(props) => <Input name="name" defaultValue={veri.name} {...props} />}
                </Field>
                <Field label="Alt alan adı" hint="Değiştirmek için destek talebi açın.">
                  {(props) => <Input value={veri.slug} disabled {...props} />}
                </Field>
                <Field label="İletişim e-postası" error={hatalar['settings.contact_email']}>
                  {(props) => (
                    <Input
                      name="contact_email"
                      type="email"
                      defaultValue={ayar?.contact_email ?? ''}
                      {...props}
                    />
                  )}
                </Field>
                <Field label="İletişim telefonu" error={hatalar['settings.contact_phone']}>
                  {(props) => (
                    <Input
                      name="contact_phone"
                      inputMode="tel"
                      defaultValue={ayar?.contact_phone ?? ''}
                      {...props}
                    />
                  )}
                </Field>
              </div>

              <Field label="Adres" error={hatalar['settings.address']}>
                {(props) => <Input name="address" defaultValue={ayar?.address ?? ''} {...props} />}
              </Field>

              <fieldset className="grid gap-3 sm:grid-cols-2">
                <legend className="pb-2 text-xs font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
                  Makbuz bilgileri
                </legend>
                <Field label="Vergi dairesi" error={hatalar['settings.tax_office']}>
                  {(props) => (
                    <Input name="tax_office" defaultValue={ayar?.tax_office ?? ''} {...props} />
                  )}
                </Field>
                <Field label="Vergi numarası" error={hatalar['settings.tax_number']}>
                  {(props) => (
                    <Input
                      name="tax_number"
                      inputMode="numeric"
                      defaultValue={ayar?.tax_number ?? ''}
                      {...props}
                    />
                  )}
                </Field>
              </fieldset>

              <fieldset className="flex flex-col gap-2">
                <legend className="pb-2 text-xs font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
                  Güvenlik
                </legend>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    name="require_mfa"
                    className="mt-1"
                    defaultChecked={ayar?.require_mfa ?? false}
                  />
                  <span className="flex flex-col">
                    <span>Yöneticiler için ikinci adım doğrulama zorunlu</span>
                    {/* Ayarı açan kişinin kendisinde de yoksa hemen kapıya
                        düşer; bunu söylemek "kendimi kilitledim" panikini önler. */}
                    <span className="text-xs text-[var(--ad-text-muted)]">
                      İkinci adımı olmayan yönetici, kurulumu yapana kadar yalnızca Güvenlik
                      ekranını görür. Sizin için de geçerli.
                    </span>
                  </span>
                </label>
              </fieldset>

              <fieldset className="flex flex-col gap-2">
                <legend className="pb-2 text-xs font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
                  Aidat
                </legend>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    name="fee_reminders_enabled"
                    className="mt-1"
                    defaultChecked={ayar?.fee_reminders_enabled ?? true}
                  />
                  <span className="flex flex-col">
                    <span>Ödenmemiş aidat için e-posta hatırlatması gönder</span>
                    <span className="text-xs text-[var(--ad-text-muted)]">
                      Borçlandırmadan 7 ve 30 gün sonra, e-postası olan üyelere. Ödeme geldiğinde
                      kendiliğinden durur.
                    </span>
                  </span>
                </label>
              </fieldset>

              <fieldset className="flex flex-col gap-3">
                <legend className="pb-2 text-xs font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
                  Destek
                </legend>
                <Field
                  label="Yeni üye talebi kime düşsün"
                  hint="Rol seçilirse o roldeki, üzerinde en az açık talep olan kişiye."
                >
                  {({ id }) => (
                    <select
                      id={id}
                      name="auto_assign"
                      defaultValue={
                        ayar?.support_auto_assign?.user_id
                          ? `user:${ayar.support_auto_assign.user_id}`
                          : ayar?.support_auto_assign?.role_code
                            ? `role:${ayar.support_auto_assign.role_code}`
                            : ''
                      }
                      className="h-9 w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-2 text-sm"
                    >
                      <option value="">Atanmasın</option>
                      <optgroup label="Kişi">
                        {(kullanicilar.data ?? [])
                          .filter((k) => k.status === 'active')
                          .map((k) => (
                            <option key={k.user_id} value={`user:${k.user_id}`}>
                              {k.full_name}
                            </option>
                          ))}
                      </optgroup>
                      <optgroup label="Rol">
                        {(roller.data ?? []).map((r) => (
                          <option key={r.code} value={`role:${r.code}`}>
                            {r.name}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  )}
                </Field>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-[var(--ad-text-muted)]">
                        <th className="py-1 pr-3 font-medium">Öncelik</th>
                        <th className="py-1 pr-3 font-medium">İlk yanıt (saat)</th>
                        <th className="py-1 pr-3 font-medium">Çözüm (saat)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ONCELIKLER.map(([kod, etiket, varsayilan]) => {
                        const mevcut = ayar?.support_sla?.[kod]
                        return (
                          <tr key={kod}>
                            <td className="py-1 pr-3">{etiket}</td>
                            <td className="py-1 pr-3">
                              <Input
                                name={`sla_${kod}_first`}
                                type="number"
                                min={1}
                                required
                                defaultValue={mevcut?.first_response_hours ?? varsayilan[0]}
                                aria-label={`${etiket} ilk yanıt saati`}
                              />
                            </td>
                            <td className="py-1 pr-3">
                              <Input
                                name={`sla_${kod}_resolution`}
                                type="number"
                                min={1}
                                required
                                defaultValue={mevcut?.resolution_hours ?? varsayilan[1]}
                                aria-label={`${etiket} çözüm saati`}
                              />
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                  <p className="pt-1 text-xs text-[var(--ad-text-muted)]">
                    Süre aşımında sorumluya (yoksa iletişim e-postasına) bir kez uyarı gider.
                    Değişiklik yalnızca yeni açılan talepleri etkiler.
                  </p>
                </div>
              </fieldset>

              <div className="flex justify-end">
                <Button type="submit" disabled={kaydet.isPending}>
                  {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
                </Button>
              </div>
            </form>
          )}
        </CardBody>
      </Card>
    </Sayfa>
  )
}

/** Öncelik kodu, etiketi ve varsayılan [ilk yanıt, çözüm] saatleri (support/sla.py ile aynı). */
const ONCELIKLER: [string, string, [number, number]][] = [
  ['urgent', 'Acil', [4, 24]],
  ['high', 'Yüksek', [8, 72]],
  ['normal', 'Normal', [24, 168]],
  ['low', 'Düşük', [72, 336]],
]

function Sayfa({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold">Ayarlar</h1>
      <AyarlarSekmeleri />
      {children}
    </div>
  )
}
